export type GeoFix = { lat: number; lng: number; accuracyM: number; at: number };

const FRESH_MS = 15_000;

const validFix = (fix: GeoFix | null | undefined): fix is GeoFix =>
  Boolean(
    fix &&
      Number.isFinite(fix.lat) &&
      Number.isFinite(fix.lng) &&
      fix.lat >= -90 &&
      fix.lat <= 90 &&
      fix.lng >= -180 &&
      fix.lng <= 180
  );

export const isFreshGeo = (fix: GeoFix | null | undefined, maxAgeMs: number = FRESH_MS): boolean =>
  validFix(fix) && Date.now() - fix.at <= maxAgeMs;

export const pickBetterGeo = (a: GeoFix | null | undefined, b: GeoFix | null | undefined): GeoFix | null => {
  const left = validFix(a) ? a : null;
  const right = validFix(b) ? b : null;
  if (!left) return right;
  if (!right) return left;
  const leftFresh = isFreshGeo(left);
  const rightFresh = isFreshGeo(right);
  if (leftFresh && !rightFresh) return left;
  if (rightFresh && !leftFresh) return right;
  return left.accuracyM <= right.accuracyM ? left : right;
};

const GEO_ATTEMPTS: PositionOptions[] = [
  { enableHighAccuracy: false, maximumAge: 300_000, timeout: 25000 },
  { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
];

const CLICK_FALLBACK_MS = 5 * 60_000;

let geoPositionInFlight: Promise<GeolocationPosition> | null = null;

const positionToFix = (pos: GeolocationPosition): GeoFix => ({
  lat: pos.coords.latitude,
  lng: pos.coords.longitude,
  accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : 9999,
  at: Date.now(),
});

export const requestGeoPosition = (): Promise<GeolocationPosition> => {
  if (geoPositionInFlight) {
    return geoPositionInFlight;
  }
  geoPositionInFlight = new Promise<GeolocationPosition>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 2, message: "This phone cannot share location." });
      return;
    }
    let attempt = 0;
    const run = () => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        (err) => {
          if (err?.code === 1 || attempt >= GEO_ATTEMPTS.length - 1) {
            reject(err);
            return;
          }
          attempt += 1;
          run();
        },
        GEO_ATTEMPTS[attempt]
      );
    };
    run();
  }).finally(() => {
    geoPositionInFlight = null;
  });
  return geoPositionInFlight;
};

export const readClickGeo = async (watched: GeoFix | null | undefined): Promise<GeoFix> => {
  try {
    const clickFix = positionToFix(await requestGeoPosition());
    const best = pickBetterGeo(clickFix, isFreshGeo(watched) ? watched : null);
    if (!best) {
      throw new Error("Wait for a GPS fix, then try again.");
    }
    return best;
  } catch (err) {
    if (validFix(watched) && Date.now() - watched.at <= CLICK_FALLBACK_MS) {
      return watched;
    }
    throw err instanceof Error ? err : new Error("Could not read location for this click. Turn on GPS and try again.");
  }
};

export const formatCaptureGeo = (fix: GeoFix | null | undefined): string => {
  if (!validFix(fix)) return "";
  return `${fix.lat.toFixed(6)}, ${fix.lng.toFixed(6)} · ±${Math.round(fix.accuracyM)}m`;
};
